import { UUID_REGEX } from "@bindery/shared/auth/authConstants";
import { RECORDING_STATUSES } from "@bindery/shared/contracts/orientation";
import { STORED_RECORDING_CONSENTS } from "@bindery/shared/contracts/study";
import * as z from "zod";
import { ORIENTATION_API_PREFIX } from "./errors";

export const RECORDING_PATH_PREFIX = `${ORIENTATION_API_PREFIX}/recording`;
export const END_SESSION_PATH = `${ORIENTATION_API_PREFIX}/end`;
export const ZOOM_WEBHOOK_PATH = `${ORIENTATION_API_PREFIX}/zoom/webhook`;

export const RECORDING_CONTROL_ACTIONS = [
  "start",
  "stop",
  "pause",
  "resume",
] as const;
export type RecordingControlAction = (typeof RECORDING_CONTROL_ACTIONS)[number];

export const RECORDING_ACTIONS = [
  ...RECORDING_CONTROL_ACTIONS,
  "consent",
] as const;
export type RecordingAction = (typeof RECORDING_ACTIONS)[number];

export const recordingPath = (action: RecordingAction): string =>
  `${RECORDING_PATH_PREFIX}/${action}`;

export const RECORDING_PATHS: readonly string[] =
  RECORDING_ACTIONS.map(recordingPath);

const sessionIdSchema = z.string().regex(UUID_REGEX);

export const recordingControlRequestSchema = z.strictObject({
  sessionId: sessionIdSchema,
});
export type RecordingControlRequest = z.infer<
  typeof recordingControlRequestSchema
>;

export const recordingControlResponseSchema = z.object({
  recordingStatus: z.enum(RECORDING_STATUSES),
});
export type RecordingControlResponse = z.infer<
  typeof recordingControlResponseSchema
>;

export const recordingConsentRequestSchema = z.strictObject({
  sessionId: sessionIdSchema,
  consent: z.enum(STORED_RECORDING_CONSENTS),
});
export type RecordingConsentRequest = z.infer<
  typeof recordingConsentRequestSchema
>;

// `revoked`: the call's recording was stopped and its files are being
// deleted, because consent was declined at some point in the call (M1).
export const recordingConsentResponseSchema = z.object({
  consent: z.enum(STORED_RECORDING_CONSENTS),
  revoked: z.boolean(),
});
export type RecordingConsentResponse = z.infer<
  typeof recordingConsentResponseSchema
>;

export const endSessionRequestSchema = z.strictObject({
  sessionId: sessionIdSchema,
});
export type EndSessionRequest = z.infer<typeof endSessionRequestSchema>;

// `ended: false` when Zoom never reported the call, so there is nothing to end.
export const endSessionResponseSchema = z.object({ ended: z.boolean() });
export type EndSessionResponse = z.infer<typeof endSessionResponseSchema>;
